import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";

import { useAuth } from "@vega-api/custom/api/hooks/useAuth";
import { useSession } from "@vega-api/custom/SessionContext/SessionContext";
import {
  FeatureFlag,
  useNoUserFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { FrontendPortal } from "@vega-api/generated/types/frontendPortal";
import { type LoginUserRequest } from "@vega-api/generated/types/loginUserRequest";
import { useGetProfile } from "@vega-api/generated/user-management-v1/user-management-v1";
import { ActionStatusMessage } from "@vega-core/components/Deprecated/ActionStatusMessage/ActionStatusMessage";
import { OuterPageContainer } from "@vega-core/components/Layout/OuterPageContainer";
import { WithLoading } from "@vega-core/components/Layout/WithLoading";
import { UnknownErrorModal } from "@vega-core/components/Modals/UnknownErrorModal";
import { NoticeType } from "@vega-core/components/Notice/Notice";
import { VEGA_ID } from "@vega-core/constants/global";
import { useTheme } from "@vega-core/context/ThemeContext/ThemeContext";
import { usePublicConfig } from "@vega-core/hooks/usePublicConfig";
import { m } from "@vega-core/paraglide/messages";
import { isF2F, isLocalSession, isMSW } from "@vega-core/utils/global";
import { trackFirstLoginSucceeded } from "@vega-core/utils/posthog/onboardingAnalytics";
import { OktaLoginButton } from "@vega-modules/components/OktaLoginButton/OktaLoginButton";
import { PublicConfigBanner } from "@vega-modules/scenes/ConfigBanner/PublicConfigBanner";
import { DevCredentialsForm } from "@vega-modules/scenes/LocalLogin/DevCredentialsForm";
import { LocalSessionForm } from "@vega-modules/scenes/LocalLogin/LocalSessionForm";

export const Login = WithLoading(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login, loginError, logout, unknownError } = useAuth();
  const session = useSession();
  const isBffAuthEnabled = useNoUserFeatureFlag(FeatureFlag.SPIKE_NEW_AUTH);
  const {
    global: { companyId, companyName },
    theme: { logos },
  } = usePublicConfig();
  const { theme } = useTheme();

  const { data: profileData, isSuccess: isProfileLoaded } = useGetProfile({
    query: {
      enabled: isBffAuthEnabled && !session.user,
      retry: false,
    },
  });

  useEffect(() => {
    if (isProfileLoaded && profileData.data && !session.user) {
      session.setUserData(profileData.data);
      trackFirstLoginSucceeded({ userId: profileData.data.id });
      navigate("/");
    }
  }, [isProfileLoaded, profileData, session, navigate]);

  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const routerError = location.state?.error;

    if (routerError && !errorMessage) {
      setErrorMessage(routerError);
      window.history.replaceState({}, "", location.pathname);
    }
  }, [location.state?.error, errorMessage, location.pathname]);

  if (session.user) {
    logout({
      isGlobal: false,
      savePath: false,
      source: FrontendPortal.GP_PORTAL,
    });
  }

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    onSubmit(values: LoginUserRequest, { setSubmitting, setTouched }) {
      login(values, () => {
        setSubmitting(false);
        setTouched({ email: false, password: false });
      });
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email(m.formValidation_invalid({ name: m.formFields_email() }))
        .required(
          m.formValidation_required({
            name: m.formFields_email(),
          }),
        ),
      password: Yup.string().required(
        m.formValidation_required({
          name: m.formFields_password(),
        }),
      ),
    }),
  });

  let credentialsContent: React.ReactNode = null;

  if (isLocalSession && !isMSW && !isF2F) {
    credentialsContent = (
      <LocalSessionForm portal={FrontendPortal.COINVEST_WORKBENCH_PORTAL} />
    );
  } else if (isMSW || isF2F) {
    credentialsContent = (
      <DevCredentialsForm formik={formik} loginError={loginError} />
    );
  }

  return (
    <OuterPageContainer hideSwitchButton isLogin>
      <PublicConfigBanner />
      <UnknownErrorModal open={!!unknownError} />
      <div className="mx-auto rounded-lg md:w-98 md:bg-background/primary md:shadow-md md:p-12">
        <div className="w-full max-w-md mx-auto">
          <div className="text-center">
            {companyId === VEGA_ID ? (
              <img
                alt="vega"
                className="w-auto h-16 mx-auto lg:mb-6"
                src={logos.smallLogoLight}
              />
            ) : (
              <img
                alt="vega"
                className="w-auto h-8 mx-auto lg:mb-6"
                src={theme === "light" ? logos.logoLight : logos.logoDark}
              />
            )}
            <h2 className="heading-4">
              {m.general_loginHeader({ companyName })}
            </h2>
            <div className="body-base-regular mt-2">
              {m.auth_oktaReSignIn()}
            </div>
          </div>
          <div className="mt-10">
            {credentialsContent}
            <div className="mt-4">
              <OktaLoginButton
                source={FrontendPortal.COINVEST_WORKBENCH_PORTAL}
              />
            </div>
          </div>
        </div>
      </div>

      {errorMessage ? (
        <div className="mt-5">
          <ActionStatusMessage
            message={errorMessage}
            type={NoticeType.ERROR}
            withContact
          />
        </div>
      ) : null}
    </OuterPageContainer>
  );
});
